import { createContext, useContext, useEffect, useState } from 'react';
import {
  getSession,
  onAuthStateChange,
  signOut as signOutService,
} from '../../modules/auth/services/authService';
import {
  callStudentRpc,
  getStudentToken,
  onStudentTokenChange,
  studentSignOut,
} from '../../modules/auth/services/studentAuthService';
import { supabase } from '../../shared/lib/supabaseClient';
import { ROLES } from '../../shared/constant/roles';
import { escapeLike } from '../../shared/utils/escapeLike';

const AuthContext = createContext(undefined);

// The teacher account named exactly "Super Admin" always gets super-admin
// access, regardless of the role it signed up with. This lets that one
// account be promoted (e.g. to see the Subjects "Add Subject" button) without
// editing its Supabase auth metadata by hand.
function isSuperAdminName(firstname, lastname) {
  return (
    String(firstname ?? '').trim().toLowerCase() === 'super' &&
    String(lastname ?? '').trim().toLowerCase() === 'admin'
  );
}

// The signed-in teacher's first name (for the dashboard greeting) is remembered in the browser,
// keyed by email, so a reload shows it immediately instead of waiting on the lookup below.
const NAME_CACHE_KEY = 'pawigrade:teacher-firstname';

function readCachedName(email) {
  try {
    const cached = JSON.parse(localStorage.getItem(NAME_CACHE_KEY) ?? 'null');
    return cached?.email === email ? cached.firstname ?? '' : '';
  } catch {
    return '';
  }
}

function writeCachedName(email, firstname) {
  try {
    localStorage.setItem(NAME_CACHE_KEY, JSON.stringify({ email, firstname }));
  } catch {
    // Storage can be blocked or full; the greeting just falls back to the lookup.
  }
}

function clearCachedName() {
  try {
    localStorage.removeItem(NAME_CACHE_KEY);
  } catch {
    // Nothing to clear.
  }
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [teacherName, setTeacherName] = useState('');
  const [teacherNameLoading, setTeacherNameLoading] = useState(true);
  // Students sign in with a session token of their own instead of Supabase Auth (studentAuthService).
  const [studentToken, setStudentToken] = useState(getStudentToken);
  // The signed-in student's own row, with the token it was loaded for: still loading while that
  // differs from the current token, so a fresh sign-in never renders as signed out.
  const [loadedStudent, setLoadedStudent] = useState({ token: null, student: null });
  // Whether the signed-in teacher still has to replace the login they were given, with the email it
  // was last looked up for. Kept while a changed username (and so email) is looked up again, so the
  // profile page isn't swapped for a spinner as it saves; cleared on sign-out.
  const [teacherLogin, setTeacherLogin] = useState({ email: null, mustChange: false });

  useEffect(() => onStudentTokenChange(setStudentToken), []);

  // An expired token clears itself (callStudentRpc), which signs the student out.
  useEffect(() => {
    let mounted = true;
    if (!studentToken) return undefined;

    callStudentRpc('student_get_self')
      .then((rows) => {
        if (mounted) setLoadedStudent({ token: studentToken, student: rows[0] ?? null });
      })
      .catch(() => {
        if (mounted) setLoadedStudent({ token: studentToken, student: null });
      });

    return () => {
      mounted = false;
    };
  }, [studentToken]);

  const student = studentToken && loadedStudent.token === studentToken ? loadedStudent.student : null;
  const studentLoading = Boolean(studentToken) && loadedStudent.token !== studentToken;

  useEffect(() => {
    let mounted = true;

    getSession()
      .then(({ data }) => {
        if (mounted) {
          setSession(data.session);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    const { data } = onAuthStateChange((_event, newSession) => {
      setSession(newSession);
    });

    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, []);

  // Supabase Auth accounts are staff only now. One still claiming the student role (an old session
  // from before students moved off Auth) is treated as signed out and cleared.
  const isStaleStudentSession = session?.user?.user_metadata?.role === ROLES.STUDENT;
  const staffSession = isStaleStudentSession ? null : session;
  const baseRole = staffSession?.user?.user_metadata?.role ?? null;
  const email = staffSession?.user?.email ?? null;

  useEffect(() => {
    if (isStaleStudentSession) signOutService().catch(() => {});
  }, [isStaleStudentSession]);

  // One lookup of the teacher's own row serves both the super-admin check and the greeting name.
  useEffect(() => {
    let mounted = true;

    if (!email) {
      setIsSuperAdmin(false);
      setTeacherName('');
      setTeacherNameLoading(false);
      setTeacherLogin({ email: null, mustChange: false });
      return undefined;
    }

    const cachedName = readCachedName(email);
    setTeacherName(cachedName);
    setTeacherNameLoading(!cachedName);
    if (baseRole === ROLES.SUPER_ADMIN) setIsSuperAdmin(true);

    supabase
      .from('teachers')
      .select('firstname, lastname, must_change_login')
      .ilike('email', escapeLike(email))
      .maybeSingle()
      .then(({ data }) => {
        if (!mounted) return;
        if (baseRole !== ROLES.SUPER_ADMIN) setIsSuperAdmin(isSuperAdminName(data?.firstname, data?.lastname));
        setTeacherName(data?.firstname ?? '');
        writeCachedName(email, data?.firstname ?? '');
        setTeacherLogin({ email, mustChange: Boolean(data?.must_change_login) });
      })
      .catch(() => {
        if (!mounted) return;
        if (baseRole !== ROLES.SUPER_ADMIN) setIsSuperAdmin(false);
        // Failing to check shouldn't lock the teacher out.
        setTeacherLogin({ email, mustChange: false });
      })
      .finally(() => {
        if (mounted) setTeacherNameLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [email, baseRole]);

  const staffRole = isSuperAdmin ? ROLES.SUPER_ADMIN : baseRole;
  const role = student ? ROLES.STUDENT : staffRole;

  // Only students and teachers are made to replace the login they were given; admins aren't.
  const isTeacher = role === ROLES.TEACHER;
  const mustChangeLogin = student
    ? Boolean(student.must_change_login)
    : isTeacher && teacherLogin.mustChange;
  const mustChangeLoginLoading = !student && isTeacher && teacherLogin.email === null;

  const value = {
    session: staffSession,
    // `user` is the staff member's Supabase Auth user, or the signed-in student's own row.
    user: student ?? staffSession?.user ?? null,
    student,
    role,
    teacherName,
    teacherNameLoading,
    loading: loading || studentLoading,
    // Set while the student or teacher is still on the username and password they were given;
    // RoleGuard keeps them on their profile page until they choose their own.
    mustChangeLogin,
    mustChangeLoginLoading,
    // Called once the profile page has saved their own login.
    markLoginChanged: () => {
      if (student) {
        setLoadedStudent((loaded) => ({ ...loaded, student: { ...loaded.student, must_change_login: false } }));
      } else {
        setTeacherLogin((current) => ({ ...current, mustChange: false }));
      }
    },
    signOut: () => {
      if (student || studentToken) return studentSignOut();
      clearCachedName();
      return signOutService();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthProvider;
